import { useEffect, useState, useMemo } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
  Collapse,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  Chip,
} from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import StorageIcon from '@mui/icons-material/Storage'
import BarChartIcon from '@mui/icons-material/BarChart'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InventoryIcon from '@mui/icons-material/Inventory'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CategoryIcon from '@mui/icons-material/Category'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import PaymentIcon from '@mui/icons-material/Payment'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import DescriptionIcon from '@mui/icons-material/Description'
import StorefrontIcon from '@mui/icons-material/Storefront'
import StoreIcon from '@mui/icons-material/Store'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import MapIcon from '@mui/icons-material/Map'
import ScheduleIcon from '@mui/icons-material/Schedule'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import SecurityIcon from '@mui/icons-material/Security'
import PolicyIcon from '@mui/icons-material/Policy'
import ArticleIcon from '@mui/icons-material/Article'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { brandColors } from '../brandColors'
import useAuthStore from '../store/authStore'
import useCreatePurchaseOrderGuardStore from '../store/createPurchaseOrderGuardStore'
import usePriceConcernBadgeStore from '../store/priceConcernBadgeStore'
import {
  ADMINISTRATION_NAV,
  canReadAdministrationItem,
  isCustomerSupportOnlyActor,
  isPromotionsOnlyActor,
  canReadSidebarPath,
} from '../utils/adminPermissions'

const ADMINISTRATION_MENU = {
  '/user-permissions': { label: 'User Permissions', icon: ManageAccountsIcon },
  '/administration/admin-passwords': { label: 'Admin passwords', icon: VpnKeyIcon },
  '/administration/stock-sync': { label: 'Stock sync', icon: SystemUpdateAltIcon },
  '/administration/promotion-sync': {
    label: 'Promotion sync',
    icon: LocalOfferIcon,
  },
  '/administration/purchasable-status-sync': {
    label: 'Purchasable status sync',
    icon: ToggleOnIcon,
  },
  '/administration/customer-support': {
    label: 'Customer Support',
    icon: SupportAgentIcon,
  },
  '/administration/default-org-location': {
    label: 'Default shop location',
    icon: LocationOnIcon,
  },
  '/payment-settings': { label: 'Payment Settings', icon: PaymentIcon },
  '/releases': { label: 'Releases', icon: SystemUpdateAltIcon },
  '/global-settings/credit-note': { label: 'Credit settings', icon: DescriptionIcon },
  '/global-settings/inventory': { label: 'Inventory thresholds', icon: InventoryIcon },
  '/administration/bulk-role-assignment': {
    label: 'Bulk role assignment',
    icon: GroupAddIcon,
  },
  '/administration/bulk-role-creation': {
    label: 'Bulk role creation',
    icon: SecurityIcon,
  },
  '/administration/critical-audit': {
    label: 'Critical Audit',
    icon: PolicyIcon,
  },
}

const ORDERS_LIST_MENU_PATH = '/orders'
const ORDERS_SUBROUTE_PREFIXES = [
  '/orders/purchase/payment-processing',
  '/orders/purchase/deposits',
]

const pathMatchesMenuItem = (menuPath, currentPath) => {
  if (menuPath === currentPath) return true
  if (menuPath === ORDERS_LIST_MENU_PATH) {
    if (!currentPath.startsWith('/orders')) return false
    return !ORDERS_SUBROUTE_PREFIXES.some(
      (prefix) =>
        currentPath === prefix || currentPath.startsWith(`${prefix}/`),
    )
  }
  if (menuPath.startsWith('/orders/')) return currentPath.startsWith(menuPath)
  if (menuPath.startsWith('/store-location-mapping')) {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath.startsWith('/reports')) {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath === '/inventory-sync') {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath === '/releases') {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath === '/administration/stock-sync') {
    return currentPath === menuPath
  }
  if (menuPath === '/administration/promotion-sync') {
    return currentPath === menuPath
  }
  if (menuPath === '/administration/purchasable-status-sync') {
    return currentPath === menuPath
  }
  if (menuPath === '/administration/customer-support') {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath === '/vendors') {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath.startsWith('/delivery-times')) {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath.startsWith('/divisions')) {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath === '/pos-store-promotions') {
    return currentPath === menuPath
  }
  if (menuPath.startsWith('/pos-store-promotions/')) {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  if (menuPath.startsWith('/content-management')) {
    return currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
  }
  return false
}

const filterMenuByPermissions = (nodes, adminPermissions) => {
  if (!Array.isArray(nodes)) return []
  return nodes
    .map((node) => {
      if (node.type === 'group' || node.type === 'subgroup') {
        const children = filterMenuByPermissions(node.children || [], adminPermissions)
        if (!children.length) return null
        return { ...node, children }
      }
      if (node.path && !canReadSidebarPath(adminPermissions, node.path)) {
        return null
      }
      return node
    })
    .filter(Boolean)
}

const getMenuGroups = (
  isSuperuserActor,
  adminPermissions,
  badgeCounts = {},
  adminRoleCodes = [],
) => {
  const priceConcernBadge = Number(badgeCounts.priceConcerns) || 0
  const administrationChildren = ADMINISTRATION_NAV.filter((item) =>
    canReadAdministrationItem(adminPermissions, item),
  )
    .map((item) => {
      const menu = ADMINISTRATION_MENU[item.path]
      return { label: menu.label, path: item.path, icon: menu.icon }
    })

  // Senior / Junior Customer Support: only Administration → Customer Support.
  if (
    isCustomerSupportOnlyActor(
      isSuperuserActor,
      adminPermissions,
      adminRoleCodes,
    )
  ) {
    const csChild = administrationChildren.find(
      (item) => item.path === '/administration/customer-support',
    )
    return [
      {
        type: 'group',
        label: 'Administration',
        icon: AdminPanelSettingsIcon,
        children: csChild
          ? [csChild]
          : [
              {
                label: 'Customer Support',
                path: '/administration/customer-support',
                icon: SupportAgentIcon,
              },
            ],
      },
    ]
  }

  if (isPromotionsOnlyActor(isSuperuserActor, adminPermissions)) {
    return [
      {
        type: 'group',
        label: 'Business',
        icon: BusinessCenterIcon,
        children: [
          {
            type: 'subgroup',
            label: 'Promotions',
            icon: LocalOfferIcon,
            children: [
              { label: 'Upload', path: '/promotions/upload', icon: CloudUploadIcon },
              { label: 'Manage', path: '/promotions/manage', icon: LocalOfferIcon },
            ],
          },
        ],
      },
      {
        type: 'group',
        label: 'POS Promotions',
        icon: LocalOfferIcon,
        children: [
          {
            type: 'subgroup',
            label: 'Upcoming Promotions',
            icon: LocalOfferIcon,
            children: [
              { label: 'Upload', path: '/pos-store-promotions/upload', icon: CloudUploadIcon },
              { label: 'Manage', path: '/pos-store-promotions/upcoming', icon: LocalOfferIcon },
            ],
          },
          {
            label: 'Actual Promotions List',
            path: '/pos-store-promotions',
            icon: LocalOfferIcon,
          },
          {
            label: 'Reports',
            path: '/pos-store-promotions/report',
            icon: BarChartIcon,
          },
        ],
      },
    ]
  }

  const groups = [
    {
      type: 'item',
      label: 'Dashboard',
      icon: DashboardIcon,
      path: '/dashboard',
    },
    {
      type: 'group',
      label: 'Supply Chain',
      icon: StorageIcon,
      children: [
        {
          type: 'subgroup',
          label: 'Products',
          icon: InventoryIcon,
          children: [
            { label: 'Upload', path: '/products/upload', icon: CloudUploadIcon },
            { label: 'Purchasable Status', path: '/products/purchasable-status', icon: ToggleOnIcon },
            { label: 'Manage', path: '/products/manage', icon: InventoryIcon },
          ],
        },
        {
          type: 'subgroup',
          label: 'Inventory',
          icon: StorageIcon,
          children: [
            { label: 'Upload', path: '/inventory/upload', icon: CloudUploadIcon },
            { label: 'Manage', path: '/inventory/manage', icon: StorageIcon },
            { label: 'Modify User', path: '/inventory/modify-user', icon: StorageIcon },
            { label: 'Create Sync', path: '/inventory-sync/create', icon: CloudUploadIcon },
            { label: 'Sync Approval', path: '/inventory-sync', icon: FactCheckIcon },
          ],
        },
        {
          type: 'subgroup',
          label: 'Delivery',
          icon: LocalShippingIcon,
          children: [
            { label: 'Upload Delivery Times', path: '/delivery-times/upload', icon: CloudUploadIcon },
            { label: 'Delivery Scheduling', path: '/delivery-times/manage', icon: ScheduleIcon },
            { label: 'Delivery Mapping', path: '/store-location-mapping', icon: MapIcon },
          ],
        },
        {
          type: 'subgroup',
          label: 'Locations',
          icon: LocationOnIcon,
          children: [
            { label: 'Upload', path: '/locations/upload', icon: CloudUploadIcon },
            { label: 'Manage', path: '/locations/manage', icon: LocationOnIcon },
          ],
        },
      ],
    },
    {
      type: 'group',
      label: 'Orders',
      icon: ShoppingCartIcon,
      children: [
        { label: 'Orders list', path: '/orders', icon: ShoppingCartIcon },
        {
          label: 'Payment processing',
          path: '/orders/purchase/payment-processing',
          icon: PaymentIcon,
        },
        {
          label: 'Deposits',
          path: '/orders/purchase/deposits',
          icon: PaymentIcon,
        },
      ],
    },
    {
      type: 'item',
      label: 'Shops',
      icon: StorefrontIcon,
      path: '/shops',
    },
    ...(isSuperuserActor
      ? [
          {
            type: 'item',
            label: 'Vendor Management',
            icon: StoreIcon,
            path: '/vendors',
          },
        ]
      : []),
    {
      type: 'group',
      label: 'Business',
      icon: BusinessCenterIcon,
      children: [
        ...(isSuperuserActor
          ? [
              {
                label: 'Price Concerns',
                path: '/price-concerns',
                icon: ReportProblemOutlinedIcon,
                badgeCount: priceConcernBadge,
              },
            ]
          : []),
        {
          type: 'subgroup',
          label: 'Promotions',
          icon: LocalOfferIcon,
          children: [
            { label: 'Upload', path: '/promotions/upload', icon: CloudUploadIcon },
            { label: 'Manage', path: '/promotions/manage', icon: LocalOfferIcon },
          ],
        },
        {
          type: 'subgroup',
          label: 'Categories',
          icon: CategoryIcon,
          children: [
            { label: 'Upload', path: '/categories/upload', icon: CloudUploadIcon },
            { label: 'Manage', path: '/categories/manage', icon: CategoryIcon },
          ],
        },
        {
          type: 'subgroup',
          label: 'Division',
          icon: AccountTreeIcon,
          children: [
            { label: 'Upload', path: '/divisions/upload', icon: CloudUploadIcon },
            { label: 'Manage', path: '/divisions/manage', icon: AccountTreeIcon },
          ],
        },
      ],
    },
    {
      type: 'group',
      label: 'POS Promotions',
      icon: LocalOfferIcon,
      children: [
        {
          type: 'subgroup',
          label: 'Upcoming Promotions',
          icon: LocalOfferIcon,
          children: [
            { label: 'Upload', path: '/pos-store-promotions/upload', icon: CloudUploadIcon },
            { label: 'Manage', path: '/pos-store-promotions/upcoming', icon: LocalOfferIcon },
          ],
        },
        {
          label: 'Actual Promotions List',
          path: '/pos-store-promotions',
          icon: LocalOfferIcon,
        },
        {
          label: 'Reports',
          path: '/pos-store-promotions/report',
          icon: BarChartIcon,
        },
      ],
    },
    {
      type: 'group',
      label: 'User Reports',
      icon: BarChartIcon,
      children: [
        { label: 'Sales Report', path: '/reports/sales', icon: BarChartIcon },
        { label: 'Inventory Stock', path: '/reports/inventory-stock', icon: StorageIcon },
      ],
    },
    ...(isSuperuserActor
      ? [
          {
            type: 'group',
            label: 'Negative Sales',
            icon: PolicyIcon,
            children: [
              {
                label: 'Negative Sales Policy',
                path: '/administration/sales-policy',
                icon: PolicyIcon,
              },
              {
                label: 'Negative Sales',
                path: '/administration/negative-sales',
                icon: PolicyIcon,
              },
            ],
          },
          {
            type: 'group',
            label: 'Content Management',
            icon: ArticleIcon,
            children: [
              {
                label: 'Login Page',
                path: '/content-management/login',
                icon: ArticleIcon,
              },
            ],
          },
        ]
      : []),
    ...(administrationChildren.length
      ? [
          {
            type: 'group',
            label: 'Administration',
            icon: AdminPanelSettingsIcon,
            children: administrationChildren,
          },
        ]
      : []),
  ]

  return filterMenuByPermissions(groups, adminPermissions)
}

const Sidebar = ({ collapsed = false, onNavigate, showCollapseToggle = false, onToggleCollapse }) => {
  const location = useLocation()
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const priceConcernBadge = usePriceConcernBadgeStore((s) => s.notReviewedCount)
  const refreshPriceConcernBadge = usePriceConcernBadgeStore((s) => s.refresh)
  const menuGroups = getMenuGroups(
    isSuperuserActor,
    adminPermissions,
    { priceConcerns: priceConcernBadge },
    adminRoleCodes,
  )
  const theme = useTheme()

  const [expandedGroups, setExpandedGroups] = useState({})
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (isSuperuserActor) {
      refreshPriceConcernBadge()
    }
  }, [isSuperuserActor, refreshPriceConcernBadge])

  // Spaces in filenames must be URL-encoded to load reliably from `public/`.
  const sidebarTopBannerSrc = `${process.env.PUBLIC_URL}/Express_Signage%20editable.png`

  const filteredMenuGroups = useMemo(() => {
    if (!searchQuery) return menuGroups

    const query = searchQuery.toLowerCase()
    return menuGroups.map(group => {
      if (group.type === 'item') {
        if (group.label.toLowerCase().includes(query)) return group
        return null
      } else if (group.type === 'group') {
        let matchingGroupChildren = []

        group.children.forEach(child => {
          if (child.type === 'subgroup') {
            const matchingSubChildren = child.children.filter(subChild => 
              subChild.label.toLowerCase().includes(query)
            )
            if (matchingSubChildren.length > 0 || child.label.toLowerCase().includes(query)) {
               matchingGroupChildren.push({
                 ...child,
                 children: child.label.toLowerCase().includes(query) ? child.children : matchingSubChildren
               })
            }
          } else {
             if (child.label.toLowerCase().includes(query)) matchingGroupChildren.push(child)
          }
        })

        if (matchingGroupChildren.length > 0 || group.label.toLowerCase().includes(query)) {
          return {
            ...group,
            children: group.label.toLowerCase().includes(query) ? group.children : matchingGroupChildren
          }
        }
        return null
      }
      return null
    }).filter(Boolean)
  }, [searchQuery, menuGroups])

  useEffect(() => {
    if (searchQuery) {
      // Auto-expand all groups that match the search query
      const newExpanded = { ...expandedGroups }
      filteredMenuGroups.forEach((group) => {
        if (group.type === 'group') {
          newExpanded[group.label] = true
          group.children.forEach(child => {
             if (child.type === 'subgroup') {
                newExpanded[`${group.label}-${child.label}`] = true
             }
          })
        }
      })
      setExpandedGroups(newExpanded)
    } else {
      // Reset expansion to the active route
      const currentPath = location.pathname
      const newExpanded = { ...expandedGroups }
      let changed = false

      menuGroups.forEach((group) => {
        if (group.type === 'group') {
          let isChildActive = false
          
          group.children.forEach(child => {
            if (child.type === 'subgroup') {
               const isSubChildActive = child.children.some((subChild) =>
                 pathMatchesMenuItem(subChild.path, currentPath)
               )
               if (isSubChildActive) {
                  isChildActive = true
                  const key = `${group.label}-${child.label}`
                  if (!expandedGroups[key]) {
                     newExpanded[key] = true
                     changed = true
                  }
               }
            } else {
               if (pathMatchesMenuItem(child.path, currentPath)) {
                 isChildActive = true
               }
            }
          })

          if (isChildActive && !expandedGroups[group.label]) {
            newExpanded[group.label] = true
            changed = true
          }
        }
      })

      if (changed) {
        setExpandedGroups(newExpanded)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, searchQuery]) // Add searchQuery so it re-evaluates auto-expansion

  // Reset search when sidebar is collapsed
  useEffect(() => {
    if (collapsed) {
      setSearchQuery('')
    }
  }, [collapsed])

  const requestLeave = useCreatePurchaseOrderGuardStore((s) => s.requestLeave)

  const handleNavClick = (event, path) => {
    if (
      location.pathname === '/orders/purchase/create' &&
      path !== location.pathname
    ) {
      const { allowed } = requestLeave(path)
      if (!allowed) {
        event.preventDefault()
        if (window.innerWidth < 900) {
          onNavigate?.()
        }
        return
      }
    }
    if (window.innerWidth < 900) {
      setSearchQuery('')
    }
    onNavigate?.()
  }

  const toggleGroup = (groupLabel) => {
    if (collapsed) return
    setExpandedGroups((prev) => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }))
  }

  const renderItem = (item, level = 0) => {
    const isActive = pathMatchesMenuItem(item.path, location.pathname)

    return (
      <ListItem key={item.path} disablePadding sx={{ mb: level > 0 ? 0.5 : 1 }}>
        <ListItemButton
          component={RouterLink}
          to={item.path}
          onClick={(event) => handleNavClick(event, item.path)}
          selected={isActive}
          sx={{
            borderRadius: 1.5,
            bgcolor: 'transparent',
            color: isActive ? brandColors.primary : '#666',
            justifyContent: collapsed && level === 0 ? 'center' : 'flex-start',
            px: collapsed && level === 0 ? 1 : (level > 0 ? 2 + level * 2 : 2),
            py: level > 0 ? 1 : 1.5,
            transition: 'all 0.3s ease',
            '&.Mui-selected': {
              bgcolor: level > 0 ? '#f5f7fa' : 'transparent',
            },
            '&.Mui-selected:hover': {
              bgcolor: '#f5f7fa',
            },
            '&:hover': {
              bgcolor: '#f5f7fa',
              color: brandColors.primary,
            },
          }}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                color: 'inherit',
                minWidth: collapsed && level === 0 ? 'auto' : 32,
                mr: collapsed && level === 0 ? 0 : 1.5,
                fontSize: level > 0 ? '1.2rem' : '1.4rem',
                transition: 'all 0.3s ease',
              }}
            >
              <item.icon fontSize="inherit" />
            </ListItemIcon>
          )}
          {(!collapsed || level > 0) && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: isActive ? 600 : 500,
                fontSize: level > 1 ? '0.85rem' : (level > 0 ? '0.9rem' : '0.95rem'),
                transition: 'all 0.3s ease',
              }}
            />
          )}
          {(!collapsed || level > 0) &&
            Number(item.badgeCount) > 0 && (
              <Chip
                size="small"
                label={item.badgeCount > 99 ? '99+' : item.badgeCount}
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: 10.5,
                  fontWeight: 800,
                  bgcolor: brandColors.primary,
                  color: '#fff',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
        </ListItemButton>
      </ListItem>
    )
  }

  const renderSubgroup = (subgroup, parentGroupLabel) => {
    const key = `${parentGroupLabel}-${subgroup.label}`
    const isExpanded = !!expandedGroups[key]
    const isAnyChildActive = subgroup.children.some((child) =>
      pathMatchesMenuItem(child.path, location.pathname)
    )

    return (
      <Box key={key}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => toggleGroup(key)}
            sx={{
              borderRadius: 1.5,
              bgcolor: 'transparent',
              color: isAnyChildActive && !isExpanded && !searchQuery ? brandColors.primary : '#666',
              px: 4,
              py: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: '#f5f7fa',
                color: brandColors.primary,
              },
            }}
          >
            {subgroup.icon && (
              <ListItemIcon
                sx={{
                  color: 'inherit',
                  minWidth: 32,
                  mr: 1.5,
                  fontSize: '1.2rem',
                  transition: 'all 0.3s ease',
                }}
              >
                <subgroup.icon fontSize="inherit" />
              </ListItemIcon>
            )}
            <ListItemText
              primary={subgroup.label}
              primaryTypographyProps={{
                fontWeight: isAnyChildActive && !searchQuery ? 600 : 500,
                fontSize: '0.9rem',
                transition: 'all 0.3s ease',
              }}
            />
            {isExpanded ? (
              <ExpandLessIcon sx={{ fontSize: '1.2rem', color: 'inherit' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: '1.2rem', color: 'inherit' }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subgroup.children.map((child) => renderItem(child, 2))}
          </List>
        </Collapse>
      </Box>
    )
  }

  const renderGroup = (group) => {
    const isExpanded = !!expandedGroups[group.label]
    let isAnyChildActive = false

    group.children.forEach(child => {
      if (child.type === 'subgroup') {
        if (child.children.some((subChild) => pathMatchesMenuItem(subChild.path, location.pathname))) {
          isAnyChildActive = true
        }
      } else {
        if (pathMatchesMenuItem(child.path, location.pathname)) {
          isAnyChildActive = true
        }
      }
    })

    const groupButton = (
      <ListItemButton
        onClick={() => toggleGroup(group.label)}
        aria-expanded={!collapsed ? isExpanded : undefined}
        sx={{
          borderRadius: 1.5,
          bgcolor: 'transparent',
          color: isAnyChildActive && !isExpanded && !searchQuery ? brandColors.primary : '#666',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 1 : 2,
          py: 1.5,
          mb: 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: '#f5f7fa',
            color: brandColors.primary,
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: 'inherit',
            minWidth: collapsed ? 'auto' : 40,
            mr: collapsed ? 0 : 1.5,
            fontSize: '1.4rem',
            transition: 'all 0.3s ease',
          }}
        >
          <group.icon fontSize="inherit" />
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={group.label}
              primaryTypographyProps={{
                fontWeight: isAnyChildActive && !searchQuery ? 600 : 500,
                fontSize: '0.95rem',
                transition: 'all 0.3s ease',
              }}
            />
            {isExpanded ? (
              <ExpandLessIcon sx={{ color: 'inherit' }} />
            ) : (
              <ExpandMoreIcon sx={{ color: 'inherit' }} />
            )}
          </>
        )}
      </ListItemButton>
    )

    return (
      <ListItem key={group.label} disablePadding sx={{ display: 'block' }}>
        {collapsed ? (
          <Tooltip title={group.label} placement="right" arrow>
            {groupButton}
          </Tooltip>
        ) : (
          groupButton
        )}
        {!collapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {group.children.map((child) => {
                if (child.type === 'subgroup') {
                  return renderSubgroup(child, group.label)
                }
                return renderItem(child, 1)
              })}
            </List>
          </Collapse>
        )}
      </ListItem>
    )
  }

  const menuList = (
    <List sx={{ width: '100%' }}>
      {filteredMenuGroups.map((item) => {
        if (item.type === 'item') {
          return collapsed ? (
            <Tooltip key={item.path} title={item.label} placement="right" arrow>
              <Box>{renderItem(item)}</Box>
            </Tooltip>
          ) : (
            renderItem(item)
          )
        } else if (item.type === 'group') {
          return renderGroup(item)
        }
        return null
      })}
      {filteredMenuGroups.length === 0 && !collapsed && (
        <Box sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}>
          No results found
        </Box>
      )}
    </List>
  )

  // Desktop-only layout: logo is a fixed-height header matching the AppBar/Toolbar height.
  if (showCollapseToggle) {
    const headerHeight = theme.mixins.toolbar?.minHeight ?? 64

    return (
      <Box sx={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',paddingTop: 1 }}>
        {/* Sidebar header (aligns with Shwapno Admin AppBar height) */}
        <Box
          sx={{
            height: headerHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={sidebarTopBannerSrc}
            alt="Shwapno Express"
            sx={{
              height: '100%',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>

        {/* Scrollable content (search + navigation) */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Box
            sx={{
              px: 1,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: collapsed ? 'flex-end' : 'flex-start',
            }}
          >
            {!collapsed && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                      bgcolor: '#f5f7fa',
                      '& fieldset': { border: 'none' },
                      '&:hover fieldset': { border: 'none' },
                      '&.Mui-focused fieldset': { border: `1px solid ${brandColors.primary}` },
                    },
                  }}
                />
              </Box>
            )}

            {/* Collapse button moved to the right side of the search row */}
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              sx={{
                color: brandColors.primary,
                '&:hover': { bgcolor: '#e8f5e9' },
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
              aria-label="Collapse sidebar"
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>

          {menuList}
        </Box>
      </Box>
    )
  }

  // Mobile/tablet: keep the existing sidebar behavior.
  return (
    <Box
      sx={{
        p: collapsed ? 1 : 2,
        pt: showCollapseToggle ? 0.5 : undefined,
        overflowY: 'auto',
        height: showCollapseToggle ? '100vh' : 'calc(100vh - 120px)',
      }}
    >
      <Box
        sx={{
          mb: collapsed ? 1.5 : 2,
          px: 1,
          display: showCollapseToggle ? 'flex' : 'block',
          alignItems: showCollapseToggle ? 'flex-start' : undefined,
          gap: showCollapseToggle ? 1 : 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="img"
            src={sidebarTopBannerSrc}
            alt="Shwapno"
            sx={{
              width: '100%',
              height: collapsed ? 34 : 'auto',
              maxHeight: collapsed ? 34 : undefined,
              objectFit: 'cover',
              display: 'block',
              borderRadius: 1,
            }}
          />
        </Box>
      </Box>

      {!collapsed && (
        <Box sx={{ mb: 2, px: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search menu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                bgcolor: '#f5f7fa',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: `1px solid ${brandColors.primary}` },
              },
            }}
          />
        </Box>
      )}

      {menuList}
    </Box>
  )
}

export default Sidebar
